// dashboard/frontend/src/components/Campaign/CampaignForm.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    IconButton,
    Select,
    MenuItem,
    Alert,
    Box,
    Switch,
    FormControlLabel,
    CircularProgress,
    Divider,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    FormHelperText,
    LinearProgress,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    FormGroup,
    Checkbox,
    Snackbar,
    Fab,
    Tooltip,
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    History as HistoryIcon,
    Add as AddIcon,
    Send as SendIcon,
    Schedule as ScheduleIcon,
    Email as EmailIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../api/axios';
import CampaignContacts from './CampaignContacts';
import CampaignRoundTab from './CampaignRoundTab';
import CreateRoundDialog from './CreateRoundDialog';

const CampaignForm = ({ campaign: initialCampaign }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const getChannelIcon = (channel) => {
        const icons = {
            email: <EmailIcon />,
            instagram_dm: <InstagramIcon />,
            twitter_dm: <TwitterIcon />,
            linkedin_dm: <LinkedInIcon />,
        };
        return icons[channel] || <EmailIcon />;
    };

    // Initialize activeTab from URL, defaulting to 0
    const [activeTab, setActiveTab] = useState(() => {
        const tabParam = searchParams.get('tab');
        return tabParam ? parseInt(tabParam, 10) : 0;
    });
    
    const [campaign, setCampaign] = useState(initialCampaign || {
        name: '',
        description: '',
        offer_description: '',
        value_proposition: '',
        email_template_guidelines: '',
        max_emails_per_day: 20,
        follow_up_enabled: false,
        follow_up_days: 7,
        sending_mode: 'manual',
        email_rotation_strategy: 'round_robin',
        schedule_start_time: '08:00',
        schedule_end_time: '18:00',
        schedule_days: [],
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gmailAccounts, setGmailAccounts] = useState([]);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);

    // Additional legacy state for email operations
    const [emailHistory, setEmailHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailContent, setEmailContent] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedContactForStatus, setSelectedContactForStatus] = useState(null);
    const [batchGenerating, setBatchGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [schedulingEmails, setSchedulingEmails] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [enrichmentStatus, setEnrichmentStatus] = useState(null);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [bulkActionDialog, setBulkActionDialog] = useState(false);
    const [bulkAction, setBulkAction] = useState('');
    const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

    // --- Legacy handlers ---
    const handleEmailHistory = async (contactId) => {
        setHistoryLoading(true);
        try {
            const response = await api.get(`/campaign-emails/?campaign=${id}&contact_assignment__contact=${contactId}`);
            setEmailHistory(response.data.results);
            setShowHistory(true);
        } catch (error) {
            console.error('Error fetching email history:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load email history',
                severity: 'error'
            });
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleGenerateEmail = async (contactId) => {
        setBatchGenerating(true);
        try {
            const response = await api.post(`/campaigns/${id}/generate_email/`, {
                contact_id: contactId
            });
            
            if (response.data.success) {
                setEmailContent(response.data.email_content);
                setEmailSubject(response.data.subject);
                setSelectedContact(contactId);
                setEmailDialogOpen(true);
            }
            
            await fetchAllData(); // Refresh contacts to show updated status
            
            setSnackbar({
                open: true,
                message: 'Email generated successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error generating email:', error);
            setSnackbar({
                open: true,
                message: 'Failed to generate email',
                severity: 'error'
            });
        } finally {
            setBatchGenerating(false);
        }
    };

    const handleSendEmail = async () => {
        setSendingEmail(true);
        try {
            const response = await api.post(`/campaigns/${id}/send_email/`, {
                contact_id: selectedContact,
                email_content: emailContent,
                subject: emailSubject
            });
            
            setEmailDialogOpen(false);
            await fetchAllData();
            
            setSnackbar({
                open: true,
                message: 'Email sent successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error sending email:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to send email',
                severity: 'error'
            });
        } finally {
            setSendingEmail(false);
        }
    };

    const handleViewInGmailDraft = async (contactEmail) => {
        try {
            const response = await api.get(`/campaigns/${id}/get_or_create_gmail_draft/`, {
                params: { contact_email: contactEmail }
            });
            
            if (response.data.draft_url) {
                window.open(response.data.draft_url, '_blank');
            }
        } catch (error) {
            console.error('Error creating Gmail draft:', error);
            setSnackbar({
                open: true,
                message: 'Failed to create Gmail draft',
                severity: 'error'
            });
        }
    };

    const handleBatchGenerate = async () => {
        setBatchGenerating(true);
        setGenerationProgress(0);
        
        try {
            const response = await api.post(`/campaigns/${id}/batch_generate/`, {
                contact_ids: selectedContacts.length > 0 ? selectedContacts : undefined
            });
            
            // Poll for progress if task ID is returned
            if (response.data.task_id) {
                setTaskId(response.data.task_id);
                pollTaskStatus(response.data.task_id);
            }
            
            setSnackbar({
                open: true,
                message: 'Batch generation started',
                severity: 'info'
            });
        } catch (error) {
            console.error('Error in batch generation:', error);
            setSnackbar({
                open: true,
                message: 'Failed to start batch generation',
                severity: 'error'
            });
            setBatchGenerating(false);
        }
    };

    const pollTaskStatus = async (taskId) => {
        const interval = setInterval(async () => {
            try {
                const response = await api.get(`/campaigns/task-status/${taskId}/`);
                setTaskStatus(response.data);
                
                if (response.data.progress) {
                    setGenerationProgress(response.data.progress);
                }
                
                if (response.data.status === 'completed' || response.data.status === 'failed') {
                    clearInterval(interval);
                    setBatchGenerating(false);
                    setGenerationProgress(0);
                    
                    if (response.data.status === 'completed') {
                        setSnackbar({
                            open: true,
                            message: 'Batch generation completed',
                            severity: 'success'
                        });
                        await fetchAllData();
                    } else {
                        setSnackbar({
                            open: true,
                            message: 'Batch generation failed',
                            severity: 'error'
                        });
                    }
                }
            } catch (error) {
                console.error('Error polling task status:', error);
                clearInterval(interval);
                setBatchGenerating(false);
            }
        }, 2000);
        
        // Clear interval after 5 minutes to prevent infinite polling
        setTimeout(() => clearInterval(interval), 300000);
    };

    const handleScheduleEmails = async () => {
        setSchedulingEmails(true);
        try {
            const response = await api.post(`/campaigns/${id}/schedule_emails/`, {
                contact_ids: selectedContacts.length > 0 ? selectedContacts : undefined
            });
            
            setShowScheduleDialog(false);
            setSnackbar({
                open: true,
                message: response.data.message || 'Messages scheduled successfully',
                severity: 'success'
            });
            
            await fetchAllData();
        } catch (error) {
            console.error('Error scheduling messages:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to schedule messages',
                severity: 'error'
            });
        } finally {
            setSchedulingEmails(false);
        }
    };

    const handleUpdateContactStatus = async (contactId, newStatus) => {
        try {
            const response = await api.patch(`/campaign-contact-assignments/${contactId}/`, {
                status: newStatus
            });
            
            await fetchAllData();
            setStatusDialogOpen(false);
            
            setSnackbar({
                open: true,
                message: 'Contact status updated',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error updating contact status:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update contact status',
                severity: 'error'
            });
        }
    };

    const handleBulkAction = async (action) => {
        setBulkAction(action);
        setBulkActionDialog(true);
    };

    const executeBulkAction = async () => {
        try {
            let response;
            switch (bulkAction) {
                case 'generate':
                    response = await api.post(`/campaigns/${id}/batch_generate/`, {
                        contact_ids: selectedContacts
                    });
                    break;
                case 'schedule':
                    response = await api.post(`/campaigns/${id}/schedule_emails/`, {
                        contact_ids: selectedContacts
                    });
                    break;
                case 'mark_sent':
                    response = await api.post(`/campaigns/${id}/mark_as_sent/`, {
                        contact_ids: selectedContacts
                    });
                    break;
                case 'remove':
                    response = await api.post(`/campaigns/${id}/remove_contacts/`, {
                        contact_ids: selectedContacts
                    });
                    break;
                default:
                    return;
            }
            
            setBulkActionDialog(false);
            setSelectedContacts([]);
            await fetchAllData();
            
            setSnackbar({
                open: true,
                message: response.data.message || 'Bulk action completed',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error executing bulk action:', error);
            setSnackbar({
                open: true,
                message: 'Failed to execute bulk action',
                severity: 'error'
            });
        }
    };

    const handleRefreshEnrichment = async () => {
        try {
            const response = await api.get(`/campaigns/${id}/enrichment_status/`);
            setEnrichmentStatus(response.data);
        } catch (error) {
            console.error('Error fetching enrichment status:', error);
        }
    };

    // --- Legacy dialogs to add at the end of the component (before closing Container tag) ---
    const renderLegacyDialogs = () => (
        <>
            {/* Email Content Dialog */}
            <Dialog
                open={emailDialogOpen}
                onClose={() => setEmailDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Email Preview</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Content"
                        value={emailContent}
                        onChange={(e) => setEmailContent(e.target.value)}
                        multiline
                        rows={10}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSendEmail}
                        variant="contained"
                        disabled={sendingEmail}
                        startIcon={sendingEmail && <CircularProgress size={20} />}
                    >
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email History Dialog */}
            <Dialog
                open={showHistory}
                onClose={() => setShowHistory(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Email History</DialogTitle>
                <DialogContent>
                    {historyLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Opens</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {emailHistory.map((email) => (
                                        <TableRow key={email.id}>
                                            <TableCell>
                                                {new Date(email.sent_at).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{email.subject}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={email.status}
                                                    color={email.status === 'sent' ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{email.open_count || 0}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowHistory(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Schedule Messages Dialog */}
            <Dialog
                open={showScheduleDialog}
                onClose={() => setShowScheduleDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Schedule Message Sending</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Messages will be sent according to your campaign scheduling settings:
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2">
                            • Days: {Array.isArray(campaign.schedule_days) ? campaign.schedule_days.join(', ') : 'Not configured'}
                        </Typography>
                        <Typography variant="body2">
                            • Time: {campaign.schedule_start_time || '08:00'} - {campaign.schedule_end_time || '18:00'}
                        </Typography>
                        <Typography variant="body2">
                            • Max per day: {campaign.max_emails_per_day || 20}
                        </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        {selectedContacts.length > 0 
                            ? `Scheduling for ${selectedContacts.length} selected contacts`
                            : 'Scheduling for all eligible contacts'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
                    <Button
                        onClick={handleScheduleEmails}
                        variant="contained"
                        disabled={schedulingEmails}
                        startIcon={schedulingEmails && <CircularProgress size={20} />}
                    >
                        {schedulingEmails ? 'Scheduling...' : 'Confirm Schedule'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contact Status Dialog */}
            <Dialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Contact Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value=""
                            onChange={(e) => handleUpdateContactStatus(selectedContactForStatus, e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="processed">Processed</MenuItem>
                            <MenuItem value="email_sent">Email Sent</MenuItem>
                            <MenuItem value="opened">Email Opened</MenuItem>
                            <MenuItem value="replied">Replied</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                            <MenuItem value="blocked">Blocked</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Action Dialog */}
            <Dialog
                open={bulkActionDialog}
                onClose={() => setBulkActionDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Bulk Action</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to {bulkAction} for {selectedContacts.length} selected contacts?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
                    <Button onClick={executeBulkAction} variant="contained" color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    // --- New state for rounds ---
    const [contacts, setContacts] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [createRoundDialog, setCreateRoundDialog] = useState(false);
    
    // --- Time options helper ---
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                options.push(timeStr);
            }
        }
        return options;
    };
    const timeOptions = generateTimeOptions();
    
    // --- Fetch all data ---
    const fetchAllData = useCallback(async () => {
        if (!id) {
            setLoading(false);
            return;
        }
        
        try {
            // Fetch campaign details
            const campaignRes = await api.get(`/campaigns/${id}/`);
            setCampaign(campaignRes.data);
            
            // Fetch Gmail accounts
            const gmailRes = await api.get('/gmail-accounts/');
            setGmailAccounts(gmailRes.data.results || []);
            
            // Fetch contacts assigned to campaign
            try {
                const contactsRes = await api.get(`/campaigns/${id}/contacts/`, {
                    params: { no_pagination: 'true' }
                });
                const contactsData = contactsRes.data.results || contactsRes.data || [];
                setContacts(Array.isArray(contactsData) ? contactsData : []);
                console.log(`Loaded ${contactsData.length} contacts for campaign ${id}`);
            } catch (error) {
                console.error('Error fetching contacts:', error);
                setContacts([]);
            }

            // Fetch rounds
            const roundsRes = await api.get(`/campaigns/${id}/rounds/`);
            setRounds(roundsRes.data || []);
            
        } catch (error) {
            console.error('Error fetching data:', error);
            setSnackbar({
                open: true,
                message: 'Failed to load campaign data',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [id]);
    
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);
    
    // --- Tab change handler ---
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Update URL with new tab value
        setSearchParams({ tab: newValue.toString() });
    };    

    // Sync tab state with URL on navigation
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam !== null) {
            const tabIndex = parseInt(tabParam, 10);
            if (tabIndex !== activeTab && !isNaN(tabIndex)) {
                setActiveTab(tabIndex);
            }
        }
    }, [searchParams]);

    // --- Save campaign (legacy) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        
        try {
            const campaignData = {
                name: campaign.name,
                description: campaign.description,
                offer_description: campaign.offer_description,
                value_proposition: campaign.value_proposition,
                email_template_guidelines: campaign.email_template_guidelines,
                max_emails_per_day: campaign.max_emails_per_day || 20,
                follow_up_enabled: campaign.follow_up_enabled || false,
                follow_up_days: campaign.follow_up_days || 7,
                sending_mode: campaign.sending_mode || 'manual',
                email_rotation_strategy: campaign.email_rotation_strategy || 'round_robin',
                schedule_start_time: campaign.schedule_start_time || '08:00',
                schedule_end_time: campaign.schedule_end_time || '18:00',
                schedule_days: campaign.schedule_days || [],
            };
            
            let response;
            if (id) {
                response = await api.put(`/campaigns/${id}/`, campaignData);
            } else {
                response = await api.post('/campaigns/', campaignData);
            }
            
            setCampaign(response.data);
            setSuccess(true);
            
            if (!id) {
                navigate(`/campaigns/${response.data.id}`);
            }
            
            setSnackbar({
                open: true,
                message: 'Campaign saved successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving campaign:', error);
            if (error.response?.data) {
                setErrors(error.response.data);
            }
            setSnackbar({
                open: true,
                message: 'Failed to save campaign',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };
    
    // --- Create new round ---
    const handleCreateRound = async (roundData) => {
        try {
            const response = await api.post(`/campaigns/${id}/rounds/`, {
                campaign: id,
                round_number: rounds.length + 1,
                ...roundData
            });
            setRounds([...rounds, response.data]);
            setCreateRoundDialog(false);
            
            const newTabIndex = 2 + rounds.length;
            setActiveTab(newTabIndex);
            setSearchParams({ tab: newTabIndex.toString() }); // Add this line
            
            setSnackbar({
                open: true,
                message: 'Round created successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error creating round:', error);
            setSnackbar({
                open: true,
                message: 'Failed to create round',
                severity: 'error'
            });
        }
    };
    
    // --- Campaign Details Tab Content (legacy) ---
    const renderCampaignDetails = () => (
        <Box component="form" onSubmit={handleSubmit}>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Campaign Name"
                            name="name"
                            value={campaign.name}
                            onChange={(e) => setCampaign({ ...campaign, name: e.target.value })}
                            error={!!errors.name}
                            helperText={errors.name}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={campaign.description}
                            onChange={(e) => setCampaign({ ...campaign, description: e.target.value })}
                            multiline
                            rows={3}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={campaign.active}
                                    onChange={(e) => setCampaign({ ...campaign, active: e.target.checked })}
                                    name="active"
                                />
                            }
                            label="Campaign Active"
                        />
                    </Grid>
                </Grid>
            </Paper>
            
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Email Content Settings</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Offer Description"
                            name="offer_description"
                            value={campaign.offer_description}
                            onChange={(e) => setCampaign({ ...campaign, offer_description: e.target.value })}
                            multiline
                            rows={3}
                            helperText="Describe what you're offering to the recipient"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Value Proposition"
                            name="value_proposition"
                            value={campaign.value_proposition}
                            onChange={(e) => setCampaign({ ...campaign, value_proposition: e.target.value })}
                            multiline
                            rows={3}
                            helperText="Explain the value you bring to the recipient"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Custom Template Guidelines"
                            name="email_template_guidelines"
                            value={campaign.email_template_guidelines || ''}
                            onChange={(e) => setCampaign({ ...campaign, email_template_guidelines: e.target.value })}
                            multiline
                            rows={4}
                            helperText="Special instructions for AI email generation (tone, style, specific phrases to use/avoid)"
                        />
                    </Grid>
                </Grid>
            </Paper>
            
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Message Scheduling Settings
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    These settings control when automated messages can be sent across all rounds
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            Send messages on these days:
                        </Typography>
                        <FormGroup row>
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                <FormControlLabel
                                    key={day}
                                    control={
                                        <Checkbox
                                            checked={Array.isArray(campaign.schedule_days) ? campaign.schedule_days.includes(day) : false}
                                            onChange={(e) => {
                                                const currentDays = Array.isArray(campaign.schedule_days) ? campaign.schedule_days : [];
                                                if (e.target.checked) {
                                                    setCampaign({
                                                        ...campaign,
                                                        schedule_days: [...currentDays, day]
                                                    });
                                                } else {
                                                    setCampaign({
                                                        ...campaign,
                                                        schedule_days: currentDays.filter(d => d !== day)
                                                    });
                                                }
                                            }}
                                        />
                                    }
                                    label={day}
                                />
                            ))}
                        </FormGroup>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Start Time</InputLabel>
                            <Select
                                value={campaign.schedule_start_time || '08:00'}
                                onChange={(e) => setCampaign({ 
                                    ...campaign, 
                                    schedule_start_time: e.target.value 
                                })}
                                label="Start Time"
                            >
                                {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>{time}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>End Time</InputLabel>
                            <Select
                                value={campaign.schedule_end_time || '18:00'}
                                onChange={(e) => setCampaign({ 
                                    ...campaign, 
                                    schedule_end_time: e.target.value 
                                })}
                                label="End Time"
                            >
                                {timeOptions.map((time) => (
                                    <MenuItem key={time} value={time}>{time}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Max Messages Per Day"
                            type="number"
                            value={campaign.max_emails_per_day || 20}
                            onChange={(e) => setCampaign({ 
                                ...campaign, 
                                max_emails_per_day: parseInt(e.target.value) || 20 
                            })}
                            helperText="Maximum automated messages to send per day across all rounds"
                        />
                    </Grid>
                </Grid>
            </Paper>
                        
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={saving}
                    startIcon={saving && <CircularProgress size={20} />}
                >
                    {saving ? 'Saving...' : (id ? 'Update Campaign' : 'Create Campaign')}
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => navigate('/campaigns')}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
    
    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }
    
    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4">
                    {id ? `Edit Campaign: ${campaign.name}` : 'Create New Campaign'}
                </Typography>
            </Box>
            
            {id && (
                <>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        variant="scrollable"           // Enable scrolling
                        scrollButtons="auto"            // Show scroll buttons when needed
                        allowScrollButtonsMobile        // Show scroll buttons on mobile
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            mb: 3,
                            // Better mobile styles
                            '& .MuiTab-root': {
                                minWidth: { xs: 'auto', sm: 120 },  // Smaller min width on mobile
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                px: { xs: 1.5, sm: 2 },
                            }
                        }}
                    >
                        <Tab label="Campaign Details" />
                        <Tab label="Contacts" />
                        {rounds.map((round, index) => (
                            <Tab 
                                key={round.id} 
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getChannelIcon(round.channel)}
                                        <span>{round.name || `Round ${round.round_number}`}</span>
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>

                    {activeTab === 0 && renderCampaignDetails()}
                    
                    {activeTab === 1 && (
                        <CampaignContacts
                            campaignId={id}
                            campaign={campaign}
                        />
                    )}
                    
                    {activeTab >= 2 && rounds[activeTab - 2] && (
                        <CampaignRoundTab
                            campaign={campaign}
                            round={rounds[activeTab - 2]}
                            contacts={contacts}
                        />
                    )}
                    
                    {/* Floating Action Button for Creating Rounds */}
                    {id && (
                        <Fab
                            color="primary"
                            aria-label="add round"
                            sx={{
                                position: 'fixed',
                                bottom: 24,
                                right: 24,
                            }}
                            onClick={() => setCreateRoundDialog(true)}
                        >
                            <AddIcon />
                        </Fab>
                    )}
                </>
            )}
            
            {!id && renderCampaignDetails()}
            
            {/* Create Round Dialog */}
            <CreateRoundDialog
                open={createRoundDialog}
                onClose={() => setCreateRoundDialog(false)}
                onCreate={handleCreateRound}
                existingRounds={rounds}
            />
            
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            {renderLegacyDialogs()}
        </Container>
    );
};

export default CampaignForm;